import { combineReducers } from 'redux';
import moment from 'moment-timezone';

import {
  SAVE_CRUD_ROWS,
  UPDATE_OFFSET,
  UPDATE_FILTER,
  UPDATING_CRUD,
  UPDATE_CRUD_FINISHED,
  SELECT_ITEM,
  DESELECT_ITEM,
  SELECT_ALL,
  DESELECT_ALL,
  SELECT_VISIBLE,
  CRUD_CLEAR_LINES,
} from 'modules/crud/actions';
import {
  CRUD_CREATE_OPEN,
  CRUD_CREATE_CANCEL,
  CRUD_CREATE_SUBMIT,
  CRUD_CREATE_SUCCESS,
  CRUD_CREATE_FAIL,
} from '../actions';
import { htmlDecode } from 'lib/utils';

export function isCreateOpen(state = false, action) {
  switch (action.type) {
    case CRUD_CREATE_OPEN:
      return true;
    case CRUD_CREATE_CANCEL:
    case CRUD_CREATE_SUCCESS:
      return false;
    default:
      return state;
  }
}

export function isSaving(state = false, action) {
  switch (action.type) {
    case CRUD_CREATE_SUBMIT:
      return true;
    case CRUD_CREATE_SUCCESS:
    case CRUD_CREATE_FAIL:
      return false;
    default:
      return state;
  }
}

export function params(state = {}, action) {
  switch (action.type) {
    case CRUD_CREATE_OPEN:
      return action.params;
    default:
      return state;
  }
}

export const PAGE_SIZE = 25;

export const defaultState = {
  offset: 0,
  filters: {},
  lines: {},
  ticks: {},
  tableData: {},
};

function textSearch(filter, value) {
  return value.toLowerCase().includes(filter.toLowerCase());
}

function filterDate(value, filterValue) {
  // When value is empty/invalid, filter out if there is at least one
  // (valid) filter. If both filters are invalid/empty, this means they are
  // cleared, and we return the value, behaving as if no filter is applied.
  if (!value.isValid()) {
    if (!filterValue.from && !filterValue.to) {
      return true;
    }
    return false;
  }

  // Date filter is inclusive, we exclude any dates that are before or
  // after the filter date
  if (filterValue.from && value.isBefore(filterValue.from)) {
    return false;
  }
  if (filterValue.to && value.isAfter(filterValue.to)) {
    return false;
  }

  return true;
}

const filteredLines = (tableData = defaultState.tableData) => {
  const filterer = (left, filter) =>
    left.filter((item) => {
      const columns = item.columns;
      const column = columns.find((c) => c.column_id === filter.field);
      if (!column) {
        return false;
      }

      switch (filter.type) {
        case 'text':
          return textSearch(filter.value, column.value);
        case 'number': {
          const value = parseInt(column.value);
          return parseInt(filter.value) === value;
        }
        case 'select': {
          let value;
          if (column.cell_type === 'Status') {
            try {
              value = htmlDecode(column.value.label);
            } catch (e) {
              // Sometimes the value is an empty string, so the above label access may fail
              value = column.value;
            }
          } else {
            value = htmlDecode(column.value);
          }
          return value === filter.value;
        }
        case 'multi_select': {
          let value;
          if (column.cell_type === 'Status') {
            try {
              value = htmlDecode(column.value.label);
            } catch (e) {
              // Sometimes the value is an empty string, so the above label access may fail
              value = column.value;
            }
          } else if (column.cell_type == 'AccessRole') {
            // value in the cell/column
            value = column.value;
            let found = false;
            if (filter.value.length != 0) {
              // if the cell has _any_ of the selected filter options, return true
              filter.value.forEach((filterValue) => {
                if (value.includes(filterValue)) {
                  found = true;
                }
              });
            } else if (filter.value.length == 0) {
              found = true;
            }
            return found;
          } else {
            value = htmlDecode(column.value);
          }

          if (filter.value.length === 0) {
            return true;
          }

          return filter.value.includes(value);
        }
        case 'text_list': {
          const value = column.value;
          // This reduce will return true if any of the names match
          // the filter's input, otherwise it'll return false
          return value.reduce(
            (prev, next) => prev || textSearch(filter.value, next.name),
            false,
          );
        }
        case 'date': {
          const value = moment(column.value, 'YYYY-MM-DD');
          const filterValue = filter.value;
          const result = filterDate(value, filterValue);

          // Check any addictional columns that are set for filtering.
          // If any of the checks pass, return true.
          const { additionalColumns } = filter;
          if (!result && additionalColumns && additionalColumns.length) {
            return additionalColumns.reduce((acc, curr) => {
              const additionalColumn = columns.find(
                (c) => c.column_id === curr,
              );
              const additionalValue = moment(
                additionalColumn.value,
                'YYYY-MM-DD',
              );
              return acc || filterDate(additionalValue, filterValue);
            }, result);
          }
          return result;
        }
        default:
          return true;
      }
    });
  const filters = Object.values(tableData.filters).filter((x) => !!x);
  const lines = Object.values(tableData.lines);
  return filters.reduce(filterer, lines);
};

export const paginatedLines = (state = defaultState) => {
  const offset = state.offset;
  const sortFunc = (x, y) => parseInt(x.index) - parseInt(y.index);
  const sorted = filteredLines(state.tableData).sort(sortFunc);
  return {
    lines: sorted.slice(offset, offset + PAGE_SIZE),
    count: sorted.length,
  };
};

export const filteredIDs = (tableData = defaultState) =>
  filteredLines(tableData).map((line) => line.primary);

export const tableData = (state = defaultState, action) => {
  switch (action.type) {
    case SAVE_CRUD_ROWS: {
      const lines = action.lines || [];
      const offset = state.offset || 0;

      return lines.reduce(
        (nextState, line) => {
          if (!nextState.lines) {
            nextState.lines = [];
          }
          const index = line.index + offset;
          nextState.lines[index] = { ...line };
          return nextState;
        },
        { ...state },
      );
    }
    case UPDATE_FILTER: {
      if (action.value === '' || action.value == null) {
        return {
          ...state,
          filters: {
            ...state.filters,
            [action.column]: undefined,
          },
        };
      }
      return {
        ...state,
        filters: {
          ...state.filters,
          [action.column]: {
            field: action.column,
            type: action.filterType,
            value: action.value,
            additionalColumns: action.additionalColumns,
          },
        },
      };
    }
    case SELECT_ITEM: {
      const id = String(action.id);
      return { ...state, ticks: { ...state.ticks, [id]: true } };
    }
    case DESELECT_ITEM: {
      const id = String(action.id);
      return { ...state, ticks: { ...state.ticks, [id]: false } };
    }
    case SELECT_ALL: {
      const ids = {};
      filteredLines(state).forEach((e) => {
        ids[String(e.primary)] = true;
      });
      return { ...state, ticks: ids };
    }
    case DESELECT_ALL: {
      return { ...state, ticks: undefined };
    }
    case SELECT_VISIBLE: {
      const visibleLines = paginatedLines({
        tableData: state,
        offset: action.offset,
      }).lines;
      const ids = {};
      visibleLines.forEach((e) => {
        ids[String(e.primary)] = true;
      });
      return { ...state, ticks: ids };
    }
    case CRUD_CLEAR_LINES:
      return { ...state, lines: {}, ticks: undefined };
    default:
      return state;
  }
};

export const offset = (state = 0, action) => {
  switch (action.type) {
    case UPDATE_OFFSET:
      return action.offset;
    default:
      return state;
  }
};

export const updating = (state = false, action) => {
  switch (action.type) {
    case UPDATING_CRUD:
      return true;
    case UPDATE_CRUD_FINISHED:
      return false;
    default:
      return state;
  }
};

export const selectedIDs = (state = defaultState) => {
  const tickState = state.ticks || {};
  const selectedIds = [];
  Object.keys(tickState).forEach((id) => {
    if (tickState[id]) {
      selectedIds.push(id);
    }
  });
  return selectedIds;
};

export const getLine = (state = defaultState, primary) =>
  Object.values(state.lines).find((line) => line.primary === primary);

const crudReducer = combineReducers({
  isCreateOpen,
  isSaving,
  params,
  tableData,
  updating,
  offset,
});
// eslint-disable-next-line import/no-default-export -- FIXME: Convert to a named export https://github.com/wegotpop/popsss/wiki/Imports#default-exports
export default crudReducer;
